import HttpFactory from "~/repository/factory";
import { ENDPOINTS } from "~/repository/endpoints";
import type {
  DocumentListScope,
  DocumentStudent,
  OrganizationDocumentProfile,
  OrganizationDocumentProfileInput,
  DocumentMissingVariablesPayload,
  DocumentMissingVariablesResponse,
  DocumentStudentActionPayload,
  DocumentTemplate,
  DocumentTemplateCopyPayload,
  DocumentTemplateCreatePayload,
  DocumentTemplateUpdatePayload,
  DocumentTemplateVersion,
  DocumentVariablesResponse,
  GeneratedDocumentsFilters,
  GeneratedDocument,
  PreviewDocumentPayload,
} from "~/types/documents";

interface PaginatedListResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

type ListResponse<T> = T[] | PaginatedListResponse<T>;

function normalizeListResponse<T>(response: ListResponse<T>): T[] {
  if (Array.isArray(response)) {
    return response;
  }

  return response.results || [];
}

class DocumentsModule extends HttpFactory {
  async listStudents(): Promise<DocumentStudent[]> {
    return await this.get(ENDPOINTS.DOCUMENTS.STUDENTS);
  }

  async getOrganizationProfile(): Promise<OrganizationDocumentProfile> {
    return await this.get(ENDPOINTS.DOCUMENTS.ORGANIZATION_PROFILE);
  }

  async updateOrganizationProfile(
    payload: OrganizationDocumentProfileInput,
  ): Promise<OrganizationDocumentProfile> {
    return await this.patch(ENDPOINTS.DOCUMENTS.ORGANIZATION_PROFILE, payload);
  }

  async listTemplates(scope?: DocumentListScope): Promise<DocumentTemplate[]> {
    const response = await this.get<ListResponse<DocumentTemplate>>(
      ENDPOINTS.DOCUMENTS.TEMPLATES,
      scope ? { scope } : {},
    );

    return normalizeListResponse(response);
  }

  async getTemplate(templateId: number): Promise<DocumentTemplate> {
    return await this.get(`${ENDPOINTS.DOCUMENTS.TEMPLATES}${templateId}/`);
  }

  async createTemplate(
    payload: DocumentTemplateCreatePayload | FormData,
  ): Promise<DocumentTemplate> {
    return await this.post(ENDPOINTS.DOCUMENTS.TEMPLATES, payload);
  }

  async updateTemplate(
    templateId: number,
    payload: DocumentTemplateUpdatePayload,
  ): Promise<DocumentTemplate> {
    return await this.patch(
      `${ENDPOINTS.DOCUMENTS.TEMPLATES}${templateId}/`,
      payload,
    );
  }

  async deleteTemplate(templateId: number): Promise<void> {
    await this.delete(`${ENDPOINTS.DOCUMENTS.TEMPLATES}${templateId}/`);
  }

  async copyTemplate(
    templateId: number,
    payload: DocumentTemplateCopyPayload = {},
  ): Promise<DocumentTemplate> {
    return await this.post(
      `${ENDPOINTS.DOCUMENTS.TEMPLATES}${templateId}/copy/`,
      payload,
    );
  }

  async downloadTemplateFile(templateId: number): Promise<Blob> {
    return await this.get(
      `${ENDPOINTS.DOCUMENTS.TEMPLATES}${templateId}/download/`,
      {},
      { responseType: "blob" },
    );
  }

  async uploadTemplateVersion(
    templateId: number,
    payload: FormData,
  ): Promise<DocumentTemplateVersion> {
    return await this.post(
      `${ENDPOINTS.DOCUMENTS.TEMPLATES}${templateId}/upload-version/`,
      payload,
    );
  }

  async listVariables(): Promise<DocumentVariablesResponse> {
    return await this.get(ENDPOINTS.DOCUMENTS.VARIABLES);
  }

  async previewTemplate(
    templateId: number,
    payload: PreviewDocumentPayload,
  ): Promise<Blob> {
    return await this.post(
      `${ENDPOINTS.DOCUMENTS.TEMPLATES}${templateId}/preview/`,
      payload,
      { responseType: "blob" },
    );
  }

  async checkMissingVariables(
    templateId: number,
    payload: DocumentMissingVariablesPayload,
  ): Promise<DocumentMissingVariablesResponse> {
    return await this.post(
      `${ENDPOINTS.DOCUMENTS.TEMPLATES}${templateId}/missing-variables/`,
      payload,
    );
  }

  async generateDocument(
    templateId: number,
    payload: DocumentStudentActionPayload,
  ): Promise<GeneratedDocument> {
    return await this.post(
      `${ENDPOINTS.DOCUMENTS.TEMPLATES}${templateId}/generate/`,
      payload,
    );
  }

  async generateDocumentFromFile(
    templateId: number,
    payload: FormData,
  ): Promise<GeneratedDocument> {
    return await this.post(
      `${ENDPOINTS.DOCUMENTS.TEMPLATES}${templateId}/generate-from-file/`,
      payload,
    );
  }

  async listGeneratedDocuments(
    filters: GeneratedDocumentsFilters = {},
  ): Promise<GeneratedDocument[]> {
    const response = await this.get<ListResponse<GeneratedDocument>>(
      ENDPOINTS.DOCUMENTS.GENERATED_DOCUMENTS,
      { ...filters },
    );

    return normalizeListResponse(response);
  }

  async getGeneratedDocument(documentId: number): Promise<GeneratedDocument> {
    return await this.get(
      `${ENDPOINTS.DOCUMENTS.GENERATED_DOCUMENTS}${documentId}/`,
    );
  }

  async downloadGeneratedDocument(documentId: number): Promise<Blob> {
    return await this.get(
      `${ENDPOINTS.DOCUMENTS.GENERATED_DOCUMENTS}${documentId}/download/`,
      {},
      { responseType: "blob" },
    );
  }

  async downloadGeneratedDocumentPdf(documentId: number): Promise<Blob> {
    return await this.get(
      `${ENDPOINTS.DOCUMENTS.GENERATED_DOCUMENTS}${documentId}/download-pdf/`,
      {},
      { responseType: "blob" },
    );
  }

  async uploadGeneratedDocumentFile(
    documentId: number,
    payload: FormData,
  ): Promise<GeneratedDocument> {
    return await this.post(
      `${ENDPOINTS.DOCUMENTS.GENERATED_DOCUMENTS}${documentId}/upload-file/`,
      payload,
    );
  }

  async deleteGeneratedDocument(documentId: number): Promise<void> {
    await this.delete(
      `${ENDPOINTS.DOCUMENTS.GENERATED_DOCUMENTS}${documentId}/`,
    );
  }
}

export default DocumentsModule;
